import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { formatTime } from '@/lib/utils';

const WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

const dateOf = (iso) => {
  const d = new Date(`${String(iso || '').slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const weekdayOf = (iso) => {
  const d = dateOf(iso);
  return d ? WEEKDAYS[d.getDay()] : '';
};

const fmtDate = (iso) => {
  const d = dateOf(iso);
  if (!d) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const hhmm = (value) => formatTime(value);

/**
 * Offene Terminanfragen der angemeldeten Lehrkraft.
 *
 * Die Antwort selbst läuft weiter über ``/teacher-slot/{token}`` — dieselbe Seite, die auch
 * der Button in der Einladungsmail öffnet. So gibt es nur eine Stelle mit der Logik für
 * Bestätigen, Alternativen vorschlagen und Ablehnen.
 */
export default function TeacherSlotRequestsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const roleNames = (user?.roles || []).map((r) => r.name);
  const isTeacher = roleNames.includes('teacher') || roleNames.includes('lehrer');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/teacher/slot-requests');
      setItems(data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Terminanfragen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isTeacher) load();
  }, [isTeacher, load]);

  if (!isTeacher) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto py-8 px-4">
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-rose-800" data-testid="teacher-slot-requests-not-authorized">
            Dieser Bereich ist Lehrkräften vorbehalten.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto py-6 px-4" data-testid="teacher-slot-requests-page">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-brand-red/5 text-brand-red flex items-center justify-center">
              <CalendarClock className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Terminanfragen</h1>
              <p className="text-sm text-slate-500">
                Die Verwaltung bittet um Rückmeldung zu diesen Terminen aus der Online-Anmeldung.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="teacher-slot-requests-reload">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>

        {loading && items.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-10 flex items-center justify-center gap-3 text-slate-500" data-testid="teacher-slot-requests-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Wird geladen …
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-500" data-testid="teacher-slot-requests-empty">
            Derzeit liegen keine offenen Terminanfragen vor.
          </div>
        ) : (
          <ul className="space-y-3" data-testid="teacher-slot-requests-list">
            {items.map((it) => (
              <li key={it.token}>
                <Link
                  to={`/teacher-slot/${encodeURIComponent(it.token)}`}
                  className="group flex items-center justify-between gap-4 bg-white rounded-xl border border-slate-200 p-4 shadow-sm transition-all hover:border-brand-red/40 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-red/40"
                  data-testid={`teacher-slot-request-${it.registration_id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">
                      {it.student_name}
                      {it.instrument ? <span className="text-slate-500 font-normal"> · {it.instrument}</span> : null}
                    </div>
                    <div className="text-sm text-slate-600 mt-0.5">
                      {it.date ? `${weekdayOf(it.date)}, ${fmtDate(it.date)}` : 'Termin offen'}
                      {it.start_time ? ` · ${hhmm(it.start_time)}` : ''}
                      {it.duration_minutes ? ` (${it.duration_minutes} Min` : ''}
                      {it.duration_minutes && it.rhythm === 'weekly' ? ', wöchentlich)' : it.duration_minutes ? ')' : ''}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-slate-400 group-hover:text-brand-red" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}
